<template src="./SideMenu.html"></template>

<script lang="ts">
import { defineComponent } from "vue";
import SideMenuItem from "../SideMenuItem";
import SideMenuTitle from "../SideMenuTitle";
import { Routes } from "@/router/routes";
import {
    isUserInRoles,
    isUserInRoleTypes,
    userHasClaims
} from "@/services/userUtils";
import { BLACK_LIST, GDPR, REPORTS } from "@/consts/customClaims";
import { roles, RoleType } from "@/consts/roles";
import MarketEmailsExportImportDialog from "@/components/MarketEmailsExportImportDialog/MarketEmailsExportImportDialog.vue";

export interface MenuItem {
    title: string;
    icon?: string;
    mdiIcon?: boolean;
    to?: string;
    exact?: boolean;
    allowedRoles?: string[];
    allowedRoleTypes?: string[];
    claims?: string[];
    onClick?: () => Promise<void>;
}

interface MenuCategory extends MenuItem {
    items?: MenuItem[];
}

export default defineComponent({
    components: { SideMenuTitle, SideMenuItem, MarketEmailsExportImportDialog },
    computed: {
        menuCategories(): MenuCategory[] {
            return [
                { title: "Dashboard", to: Routes.Home },
                {
                    title: "Cases",
                    items: [
                        {
                            title: "Overview",
                            to: Routes.CaseOverview,
                            icon: "menu",
                            mdiIcon: true
                        },
                        {
                            title: "Create Case",
                            to: Routes.CaseCreate,
                            icon: "chat-plus-outline",
                            mdiIcon: true,
                            allowedRoleTypes: [
                                RoleType.IFM_Admin,
                                RoleType.BMW_Admin,
                                RoleType.Agent
                            ]
                        },
                        {
                            title: "GDPR Search",
                            to: Routes.GdprSearch,
                            icon: "account-search-outline",
                            mdiIcon: true,
                            claims: [GDPR]
                        },
                        {
                            title: "Favorites",
                            to: Routes.Favorites,
                            icon: "star-outline",
                            mdiIcon: true
                        }
                    ]
                },
                {
                    title: "Configuration",
                    items: [
                        {
                            title: "Users",
                            icon: "users",
                            to: Routes.Users,
                            allowedRoleTypes: [
                                RoleType.IFM_Admin,
                                RoleType.BMW_Admin
                            ]
                        },
                        {
                            title: "Markets",
                            icon: "markets",
                            to: Routes.Markets,
                            allowedRoleTypes: [
                                RoleType.IFM_Admin,
                                RoleType.BMW_Admin
                            ]
                        },
                        {
                            title: "Rules",
                            icon: "drama-masks",
                            to: Routes.Rules,
                            mdiIcon: true,
                            allowedRoleTypes: [RoleType.IFM_Admin]
                        },
                        {
                            title: "Blacklist",
                            icon: "file-alert-outline",
                            to: Routes.Blacklist,
                            mdiIcon: true,
                            claims: [BLACK_LIST]
                        },
                        {
                            title: "Monthly report",
                            icon: "calendar-check",
                            to: Routes.MonthlyReport,
                            mdiIcon: true,
                            allowedRoles: [roles.admin, roles.bmwBccTeam]
                        }
                    ]
                },
                {
                    title: "Reporting",
                    items: [
                        {
                            title: "Daily Cases",
                            icon: "car",
                            to: Routes.DailyReport,
                            claims: [REPORTS]
                        },
                        {
                            title: "YTD",
                            icon: "chart_bar",
                            to: Routes.YtdReport,
                            claims: [REPORTS]
                        }
                    ]
                },
                {
                    title: "Billing",
                    items: [
                        {
                            to: Routes.BillingAccount,
                            exact: true,
                            title: "Account",
                            icon: "card-account-details-outline",
                            mdiIcon: true,
                            allowedRoleTypes: [RoleType.IFM_Admin]
                        },
                        {
                            to: Routes.Billing,
                            exact: true,
                            title: "Billing report",
                            icon: "newspaper-variant-outline",
                            mdiIcon: true,
                            allowedRoleTypes: [RoleType.IFM_Admin]
                        }
                    ]
                }
            ].filter(this.isMenuCategoryVisible);
        }
    },
    methods: {
        isMenuItemVisible(
            roles?: string[],
            roleTypes?: string[],
            claims?: string[]
        ): boolean {
            if (roles && !isUserInRoles(roles)) return false;
            if (roleTypes && !isUserInRoleTypes(roleTypes)) return false;
            if (claims && !userHasClaims(claims)) return false;
            return true;
        },
        isMenuCategoryVisible(category: MenuCategory): boolean {
            return (
                category.items?.some((i) =>
                    this.isMenuItemVisible(
                        i.allowedRoles,
                        i.allowedRoleTypes,
                        i.claims
                    )
                ) ?? false
            );
        }
    }
});
</script>

<style src="./SideMenu.scss" lang="scss" scoped></style>
